import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import StarIcon from '../common/StarIcon';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(false);

  // Refs for focus trap
  const menuRef = useRef(null);
  const firstFocusableElementRef = useRef(null);
  const lastFocusableElementRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen((isOpen) => !isOpen);
  };

  // Effect to handle scroll events — passive for jank-free scrolling.
  useEffect(() => {
    const handleScroll = () => {
      setIsNavbarCollapsed(window.scrollY > 80);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Keep the body scroll lock in sync with the menu state.
  useEffect(() => {
    document.body.classList.toggle('menu-open', isMenuOpen);

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);
  
  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMenuOpen) return;
    
    const menuElement = menuRef.current;
    if (!menuElement) return;
    
    // Get all focusable elements in the menu
    const focusableElements = menuElement.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );
    
    if (focusableElements.length === 0) return;
    
    // Set first and last focusable elements
    firstFocusableElementRef.current = focusableElements[0];
    lastFocusableElementRef.current = focusableElements[focusableElements.length - 1];
    
    // Focus the first element when menu opens
    firstFocusableElementRef.current.focus();
    
    // Handle keyboard navigation
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      
      // If shift + tab and focus is on first element, move to last element
      if (e.shiftKey && document.activeElement === firstFocusableElementRef.current) {
        e.preventDefault();
        lastFocusableElementRef.current.focus();
      }
      
      // If tab and focus is on last element, move to first element
      if (!e.shiftKey && document.activeElement === lastFocusableElementRef.current) {
        e.preventDefault();
        firstFocusableElementRef.current.focus();
      }
    };
    
    // Add event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Clean up
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  return (
    <header className="site-header">
      
      <div className="header-announcement-bar-wrapper">
        <div className="header-border"></div>
        <div className="header-dropshadow"></div>
        
        <div className="header-background">
          <div className="header-background-solid"></div>
        </div>
        
        <div className={`header-inner ${isNavbarCollapsed ? 'collapsed' : ''}`}>
          <div className="header-background-blur"></div>

          {/* Single header layout — scales fluidly across viewports.
              Inline nav links collapse at the tablet breakpoint; everything
              else (logo, order CTA, burger) is identical on all sizes. */}
          <div className="header-row">
            <div className="header-title">
              <Link to="/" aria-label="Star Deli Home - Tray Catering">
                <img
                  src="/logos/star-logo.png"
                  alt="Star Deli — Tray Catering"
                  className="header-logo-img"
                  width="180"
                  height="60"
                />
              </Link>
            </div>

            <nav className="header-nav-list" aria-label="Primary">
              <NavLink to="/" end>Home</NavLink>
              <NavLink to="/menu">Sandwiches</NavLink>
              <NavLink to="/deli">Deli Menu</NavLink>
              <NavLink to="/catering">Catering</NavLink>
              <NavLink to="/about">About Us</NavLink>
              <NavLink to="/games">Starcade</NavLink>
            </nav>

            <div className="header-actions">
              <a
                href="tel:248-352-7377"
                className="btn primary header-order-btn"
                aria-label="Order now — call (248) 352-7377"
              >
                <StarIcon name="phone" aria-hidden="true" />
                <span className="header-order-btn-label"> Order Now</span>
              </a>

              <button
                className="header-burger-btn"
                onClick={toggleMenu}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
              >
                <div className="burger-box">
                  <div className={`burger-inner ${isMenuOpen ? 'open' : ''}`}>
                    <div className="top-bun"></div>
                    <div className="patty"></div>
                    <div className="bottom-bun"></div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu"
        className={`menu-overlay ${isMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal={isMenuOpen}
        aria-label="Main Navigation"
        ref={menuRef}
      >
        <div className="menu-overlay-inner">
          <div className="menu-overlay-content">
            <div className="mobile-nav-links">
              <NavLink to="/" end onClick={toggleMenu}>
                Home <StarIcon name="chevronRight" />
              </NavLink>
              <NavLink to="/menu" onClick={toggleMenu}>
                Sandwiches <StarIcon name="chevronRight" />
              </NavLink>
              <NavLink to="/deli" onClick={toggleMenu}>
                Deli Menu <StarIcon name="chevronRight" />
              </NavLink>
              <NavLink to="/catering" onClick={toggleMenu}>
                Catering <StarIcon name="chevronRight" />
              </NavLink>
              <NavLink to="/about" onClick={toggleMenu}>
                About Us <StarIcon name="chevronRight" />
              </NavLink>
              <NavLink to="/games" onClick={toggleMenu}>
                Starcade <StarIcon name="chevronRight" />
              </NavLink>
              {/* Contact information is in the footer and mobile contact section below */}
            </div>
            
            <div className="mobile-contact-info">
              <div className="mobile-phone">
                <h4>Order by Phone</h4>
                <a href="tel:248-352-7377">248-352-7377</a>
              </div>
              
              <div className="mobile-social">
                <h4>Connect With Us</h4>
                <div className="social-icons">
                  <a href="https://facebook.com" aria-label="Facebook" rel="noopener noreferrer" target="_blank">
                    <StarIcon name="facebook" />
                  </a>
                  <a href="https://instagram.com" aria-label="Instagram" rel="noopener noreferrer" target="_blank">
                    <StarIcon name="instagram" />
                  </a>
                  <a href="https://twitter.com" aria-label="Twitter" rel="noopener noreferrer" target="_blank">
                    <StarIcon name="twitter" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
